import { useQueryClient } from '@tanstack/react-query';
import { createContext, ReactNode, useContext } from 'react';

import { authClient, UserModel } from '@merchant-clients/auth';

import { useSubmit } from '@frontend/hooks';

import { useRoot } from './root-context';

export interface UserContextState {
  user: UserModel;
  logout(): void;
  refetch(completed?: () => void): void;
  updateCache(update: Partial<UserModel>): void;
  updateUser(user: Partial<UserModel>): void;
}

export const UserContext = createContext<UserContextState>({
  logout: () => {
    // No-op outside UserProvider.
  },
  refetch: () => {
    // No-op outside UserProvider.
  },
  updateCache: () => {
    // No-op outside UserProvider.
  },
  updateUser: () => {
    // No-op outside UserProvider.
  },
  user: undefined as unknown as UserModel,
});

export interface OptionalUserContextState {
  user?: UserModel;
  logout(): void;
  refetch(completed?: () => void): void;
  updateCache(update: Partial<UserModel>): void;
  updateUser(user: Partial<UserModel>): void;
}

export interface UserProviderProps {
  children: ReactNode;
  user?: UserModel;
  setUser: React.Dispatch<React.SetStateAction<UserModel | undefined>>;
}

export const UserProvider = ({ children, ...props }: UserProviderProps) => {
  const queryClient = useQueryClient();
  const { removeLocalSession } = useRoot();

  const { submit: logout } = useSubmit<void, void>({
    async onSubmit() {
      await authClient.logout();
      removeLocalSession();
    },
    onError(e) {
      console.error(e);
      // If we fail for some reason to logout we delete the local session
      // token so the user does not end up in a weird state where they can't
      // log out. The token will later be invalidated when it expires instead.
      removeLocalSession();
    },
  });

  const updateCache = (update: Partial<UserModel>) => {
    queryClient.setQueryData(['user'], (state: UserModel) => {
      if (!state || !update) {
        return state;
      }

      return { ...state, ...update };
    });
  };

  return (
    <UserContext.Provider
      value={{
        user: props.user!,
        updateUser: (user) => {
          props.setUser((prev) => ({ ...prev!, ...user }));
        },
        logout,
        updateCache,
        refetch: (completed) => {
          queryClient.invalidateQueries({ queryKey: ['user'] }).finally(() => {
            completed?.();
          });
        },
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextState => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  if (!context.user) {
    throw new Error('User is undefined. Did you mean to use useOptionalUser?');
  }

  return context;
};

export const useOptionalUser = (): OptionalUserContextState => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }

  return useContext(UserContext) as OptionalUserContextState;
};
